import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, CalendarDays, Repeat, PlusCircle, Flag, Wallet, ArrowRight } from "lucide-react";
import { useI18n } from "../../i18n/LanguageProvider";
import { Container } from "../Container";

const ease = [0.22, 1, 0.36, 1];
const spring = { type: "spring", stiffness: 120, damping: 18 };

const PHOTOS = [
  "/people/p1.jpg",
  "/people/p2.jpg",
  "/people/p3.jpg",
  "/people/p4.jpg",
  "/people/p5.jpg",
  "/people/p6.jpg",
];

const ICONS = [Wallet, CalendarDays, Check, Repeat, PlusCircle, Flag];

// Language-neutral demo figures per method.
const AMOUNTS = ["420,00 €", "150,00 €", "380,00 €", "49,00 €", "45,00 €", "1.200,00 €"];

const ROTATE_MS = 4600;

function Row({ label, value, tone = "muted", strong = false }) {
  const toneCls =
    tone === "green"
      ? "text-green"
      : tone === "amber"
      ? "text-amber-500"
      : "text-black/45";
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-black/60">{label}</span>
      <span className={`text-sm font-semibold ${strong ? "text-black" : toneCls}`}>{value}</span>
    </div>
  );
}

function MethodUI({ index, ui }) {
  switch (index) {
    case 0: // Pago anticipado — full amount charged upfront
      return (
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-black/40">{ui.upfront}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-green/10 px-2.5 py-1 text-[11px] font-semibold text-green">
              <Check className="h-3 w-3" strokeWidth={3} /> {ui.paid}
            </span>
          </div>
          <div className="mt-3 text-3xl font-semibold tracking-tight text-black">420,00 €</div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
            <div className="h-full w-full brand-gradient" />
          </div>
        </div>
      );
    case 1: // Reserva + saldo
      return (
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-black">{ui.total}</span>
            <span className="text-sm font-semibold text-black">420,00 €</span>
          </div>
          <div className="mt-2 divide-y divide-black/5 border-t border-black/5">
            <Row label={ui.deposit} value="150,00 €" tone="green" />
            <Row label={ui.balance} value="270,00 €" tone="amber" />
          </div>
          <div className="mt-3 flex gap-1.5">
            <span className="h-1.5 flex-[0.36] rounded-full brand-gradient" />
            <span className="h-1.5 flex-[0.64] rounded-full bg-black/10" />
          </div>
        </div>
      );
    case 2: // Pago al finalizar
      return (
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-green/10 text-green">
              <Check className="h-4 w-4" strokeWidth={3} />
            </span>
            <div>
              <div className="text-sm font-semibold text-black">{ui.completed}</div>
              <div className="text-xs text-black/45">{ui.dueOnFinish}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between rounded-xl bg-black/[0.03] px-3 py-2.5">
            <span className="text-sm text-black/60">{ui.paid}</span>
            <span className="text-sm font-semibold text-green">380,00 €</span>
          </div>
        </div>
      );
    case 3: // Pago recurrente
      return (
        <div>
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-black">
              <Repeat className="h-4 w-4 text-green" strokeWidth={2} /> {ui.monthly}
            </span>
            <span className="text-sm font-semibold text-black">49,00 €</span>
          </div>
          <div className="mt-3 flex items-center justify-between rounded-xl bg-black/[0.03] px-3 py-2.5">
            <span className="text-xs text-black/45">{ui.nextCharge}</span>
            <span className="text-xs font-semibold text-black/70">01 · 08 · 15</span>
          </div>
          <div className="mt-3 flex gap-1.5">
            {[1, 1, 1, 0, 0].map((f, i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full ${f ? "brand-gradient" : "bg-black/10"}`} />
            ))}
          </div>
        </div>
      );
    case 4: // Extras posteriores
      return (
        <div>
          <Row label={ui.total} value="300,00 €" strong />
          <div className="mt-1 flex items-center justify-between rounded-xl border border-dashed border-green/40 bg-green/[0.04] px-3 py-2.5">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green">
              <PlusCircle className="h-4 w-4" strokeWidth={2} /> {ui.extra}
            </span>
            <span className="text-sm font-semibold text-green">+45,00 €</span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-black/45">{ui.total}</span>
            <span className="text-sm font-semibold text-black">345,00 €</span>
          </div>
        </div>
      );
    case 5: // Pago por hitos
    default:
      return (
        <div>
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-black">
              <Flag className="h-4 w-4 text-green" strokeWidth={2} /> {ui.milestone} 2 / 4
            </span>
            <span className="text-sm font-semibold text-black">1.200,00 €</span>
          </div>
          <div className="mt-4 flex items-center gap-1.5">
            {[1, 1, 0, 0].map((f, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-1.5">
                <span className={`h-2 w-full rounded-full ${f ? "brand-gradient" : "bg-black/10"}`} />
                <span className={`text-[10px] font-semibold ${f ? "text-green" : "text-black/30"}`}>{300 * (i + 1)} €</span>
              </div>
            ))}
          </div>
        </div>
      );
  }
}

export function PaymentPolicies() {
  const { t } = useI18n();
  const methods = t("policies.methods");
  const ui = t("policies.ui");
  const n = Array.isArray(methods) ? methods.length : 6;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => setActive((v) => (v + 1) % n), ROTATE_MS);
    return () => clearTimeout(timer.current);
  }, [active, paused, n]);

  const select = (i) => {
    setActive(i);
    setPaused(true);
    clearTimeout(timer.current);
  };

  const Icon = ICONS[active];
  const m = methods[active] || {};

  return (
    <section id="policies" className="scroll-mt-24 bg-[#F6F8F7] py-24 md:py-36">
      <Container>
        <div className="mb-4 flex items-center gap-3">
          <span className="h-1.5 w-1.5 rounded-full brand-gradient" />
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-black/45">{t("policies.label")}</span>
        </div>

        <div className="grid grid-cols-1 gap-14 md:grid-cols-[0.92fr_1.08fr] md:gap-10 lg:gap-20">
          {/* Left — heading + method list */}
          <div className="lg:pt-6">
            <h2 className="max-w-xl text-3xl font-semibold leading-[1.1] tracking-tight text-black sm:text-4xl lg:text-5xl">
              {t("policies.title")}
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-black/55 md:text-lg">{t("policies.subtitle")}</p>

            <div className="mt-10 flex flex-col">
              {methods.map((method, i) => {
                const MIcon = ICONS[i];
                const on = active === i;
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`policy-method-${i}`}
                    onClick={() => select(i)}
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    className="group relative flex items-start gap-4 border-t border-black/[0.08] py-4 text-left last:border-b"
                  >
                    <span
                      className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl transition-colors duration-300 ${
                        on ? "brand-gradient text-white" : "bg-black/[0.04] text-black/40 group-hover:text-black/70"
                      }`}
                    >
                      <MIcon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <span className="flex-1">
                      <span className={`block text-base font-semibold tracking-tight transition-colors ${on ? "text-black" : "text-black/50 group-hover:text-black/80"}`}>
                        {method.name}
                      </span>
                      <AnimatePresence initial={false}>
                        {on && (
                          <motion.span
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.32, ease }}
                            className="block overflow-hidden"
                          >
                            <span className="mt-1.5 block text-sm leading-relaxed text-black/55">{method.desc}</span>
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                    {on && !paused && (
                      <motion.span
                        key={active}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
                        style={{ originX: 0 }}
                        className="absolute -top-px left-0 h-0.5 w-full brand-gradient"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right — morphing composition (person + floating pill + UI card) */}
          <div className="flex justify-center md:justify-end">
            <div
              className="relative w-full max-w-[420px]"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              {/* soft glow */}
              <div className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-green/10 blur-3xl" />

              {/* person */}
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[2.2rem] bg-black/5 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.4)]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={PHOTOS[active]}
                    alt={`Centauros — ${m.name || t("policies.label")}`}
                    loading="lazy"
                    decoding="async"
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.6, ease }}
                    className="h-full w-full object-cover"
                    draggable="false"
                  />
                </AnimatePresence>

                {/* floating status pill */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`pill-${active}`}
                    initial={{ opacity: 0, y: -14, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={spring}
                    className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-3.5 py-2 shadow-lg backdrop-blur"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full brand-gradient text-white">
                      <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                    <span className="text-xs font-semibold text-black">{m.name}</span>
                    <span className="text-xs font-bold text-green">+{AMOUNTS[active]}</span>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* floating UI recreation card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`ui-${active}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 12 }}
                  transition={{ duration: 0.45, ease }}
                  className="absolute -bottom-8 -left-4 w-[72%] max-w-[280px] rounded-2xl border border-black/[0.06] bg-white p-4 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)] sm:-left-8"
                  data-testid="policy-ui-card"
                >
                  <MethodUI index={active} ui={ui} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
